import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API, BACKEND_URL } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { User, Camera, Phone, Mail, Edit3, Check, LogOut, Heart, Bell, ImageIcon, Repeat, ChevronRight } from 'lucide-react';
import LazyImage from '../components/LazyImage';

import AppLogo from '../components/AppLogo';

const ProfilePage = () => {
  const { user, logout, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({ name: '', bio: '', phone: '', profile_picture: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        bio: user.bio || '',
        phone: user.phone || '',
        profile_picture: user.profile_picture || ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    try {
      const res = await axios.put(`${API}/profile`, {
        name: profile.name,
        bio: profile.bio,
        phone: profile.phone,
      });
      login(res.data);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Could not save profile');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10MB)'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const path = uploadRes.data.path;
      const res = await axios.put(`${API}/profile`, { profile_picture: path });
      setProfile(prev => ({ ...prev, profile_picture: path }));
      login(res.data);
      toast.success('Photo updated!');
    } catch (error) {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const avatarUrl = profile.profile_picture ? `${BACKEND_URL}/api/files/${profile.profile_picture}` : null;

  return (
    <div className="min-h-screen pb-24" data-testid="profile-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center gap-3">
          <AppLogo />
          <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Profile</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6 space-y-6">
        {/* Avatar & Name Header */}
        <section className="surface-card p-6 text-center" data-testid="profile-header">
          <div className="relative w-24 h-24 mx-auto mb-4">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center overflow-hidden shadow-lg">
              {avatarUrl ? (
                <LazyImage
                  src={avatarUrl}
                  alt="Profile"
                  className="w-full h-full rounded-full overflow-hidden"
                  data-testid="profile-avatar-img"
                  fallback={<span className="text-3xl font-bold text-white">{user?.name?.charAt(0)?.toUpperCase()}</span>}
                />
              ) : (
                <span className="text-3xl font-bold text-white">{user?.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 w-8 h-8 bg-card rounded-full shadow-md flex items-center justify-center hover:bg-muted transition-colors"
              data-testid="upload-avatar-btn"
              disabled={uploading}
            >
              {uploading ? (
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-cyan-200 border-t-cyan-600" />
              ) : (
                <Camera className="w-4 h-4 text-cyan-600" />
              )}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} data-testid="avatar-file-input" />
          </div>
          <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{user?.name}</h2>
          <p className="text-xs text-muted-foreground capitalize mt-0.5">{user?.role === 'care_partner' ? 'Supporter' : user?.role?.replace('_', ' ')}</p>
          {user?.bio && !editing && <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">{user.bio}</p>}
        </section>

        {/* Editable Fields */}
        <section className="surface-card p-5 space-y-4" data-testid="profile-fields">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-foreground">Personal Info</h3>
            {editing ? (
              <Button size="sm" onClick={handleSave} className="btn-primary h-8 text-xs" data-testid="save-profile-btn">
                <Check className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-8 text-xs rounded-full" data-testid="edit-profile-btn">
                <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3 py-2">
              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              {editing ? (
                <Input data-testid="profile-name-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="flex-1 rounded-xl" placeholder="Your name" />
              ) : (
                <div className="flex-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Name</p>
                  <p className="text-sm font-medium text-foreground">{user?.name}</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 py-2">
              <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Email</p>
                <p className="text-sm font-medium text-foreground">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 py-2">
              <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              {editing ? (
                <Input data-testid="profile-phone-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="flex-1 rounded-xl" placeholder="Phone number" />
              ) : (
                <div className="flex-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Phone</p>
                  <p className="text-sm font-medium text-foreground">{user?.phone || 'Not set'}</p>
                </div>
              )}
            </div>

            <div className="flex items-start gap-3 py-2">
              <Heart className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              {editing ? (
                <div className="flex-1">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Bio</Label>
                  <Textarea data-testid="profile-bio-input" value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} className="mt-1 resize-none rounded-xl" placeholder="Tell your circle a bit about yourself..." rows={3} maxLength={200} />
                  <span className="text-[10px] text-muted-foreground/50 float-right">{profile.bio?.length || 0}/200</span>
                </div>
              ) : (
                <div className="flex-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Bio</p>
                  <p className="text-sm font-medium text-foreground">{user?.bio || 'Not set'}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="surface-card overflow-hidden" data-testid="profile-links">
          {[
            { icon: Bell, label: 'Notification Settings', path: '/notification-settings', color: 'text-cyan-600' },
            { icon: ImageIcon, label: 'Photo Gallery', path: '/photos', color: 'text-pink-600' },
            { icon: Repeat, label: 'Routines & Medications', path: '/recurring-tasks', color: 'text-violet-600' },
          ].map(link => (
            <button key={link.path} onClick={() => navigate(link.path)} className="w-full flex items-center justify-between px-5 py-3.5 border-b border-cyan-50 last:border-0 hover:bg-cyan-50/30 transition-colors" data-testid={`link-${link.path.slice(1)}`}>
              <div className="flex items-center gap-3">
                <link.icon className={`w-4 h-4 ${link.color}`} />
                <span className="text-sm font-medium text-foreground">{link.label}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          ))}
        </section>

        {/* Logout */}
        <section className="surface-card p-4">
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </section>
      </main>
    </div>
  );
};

export default ProfilePage;
